import {ID} from "../model/id"
import * as React from "react"
import {
  CommonPlaceFragment,
  CommonPlaces,
  CommonPlaces as COMMON_PLACES,
  DefaultLocale as DEFAULT_LOCALE,
  DeleteCommonPlace as DELETE_COMMON_PLACE,
  PlaceSuggest as PLACE_SUGGESTIONS,
  UpsertCommonPlace as UPSERT_COMMON_PLACE,
  UpsertCommonPlaceMutationVariables,
  UUIDObjFragment,
} from "../graphql/queries"
import {CommonPlace, ScratchCommonPlace} from "../model/places/PlaceTypes"
import {CommonPlaceRow} from "./CommonPlaceRow"
import {PlaceMap} from "./PlaceMap"
import {Locale} from "../model/BookingTypes"
import {CommonPlaceForm} from "./CommonPlaceForm"
import {Form, Grid, Header, Loader, Segment, Table} from "semantic-ui-react"
import {CommonPlaceSearch} from "./CommonPlaceSearch"
import {PlaceView} from "./PlaceView"
import {emptyCommonPlace, toScratchCommonPlace} from "../model/places/place"
import {GraphQL, makeQueryWithVarsNoT} from "../graphql"
import {errorUnionToEither, OrError} from "../graphql/errors"
import {WithModalStore} from "../App"
import {DeleteCommonPlaceModal} from "./DeleteCommonPlaceModal"

interface State {
  searchPlaceId: ID | null
  mapPlaceId: ID | null
  locale: Locale | null
  commonPlace: ScratchCommonPlace | null
}

type Props = DefaultLocaleProp & WithCommonPlacesProp & WithModalStore

class CommonPlacesAdmin extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      searchPlaceId: null,
      mapPlaceId: null,
      locale: null,
      commonPlace: null,
    }
  }

  handleCommonPlaceRowSelect = (index: number) => {
    const commonPlace = this.props.commonPlaces[index]
    this.onCommonPlace(commonPlace)
    this.setState({
      locale: commonPlace.locale,
      mapPlaceId: commonPlace.placeId,
    })
  }

  handleCommonPlaceRowDelete = (index: number) => {
    const {commonPlaces} = this.props
    this.onDeleteCommonPlace(commonPlaces[index])
  }

  handleAddCommonPlace = () => {
    const {searchPlaceId} = this.state

    const locale = this.defaultLocale()
    if (locale && searchPlaceId) {
      this.onAddCommonPlace(locale.id, searchPlaceId)
    }
  }

  handleSearchPlaceId = (searchPlaceId: ID) =>
    this.setState({
      searchPlaceId,
      mapPlaceId: searchPlaceId,
    })

  handleLocale = (locale: Locale) => this.setState({locale})

  defaultLocale = () => this.state.locale || this.props.defaultLocale

  render() {
    const {commonPlaces, defaultLocale, modalStore} = this.props

    // TODO Fix this when we build adding (deleting?)
    if (commonPlaces.length === 0) {
      return <div>There are no common places.</div>
    }

    const {commonPlace, searchPlaceId, mapPlaceId} = this.state
    const commonPlaceId = commonPlace ? commonPlace.id : null
    const locale = this.defaultLocale()

    return (
      <Form>
        <Header as="h3">Common Places</Header>
        <Grid stackable columns={2}>
          <Grid.Row>
            <Grid.Column>
              <Segment.Group>
                <Segment inverted color="olive">
                  <Header as="h5">Place Search</Header>
                  <CommonPlaceSearch
                    locale={locale}
                    onPlaceId={this.handleSearchPlaceId}
                    onLocale={this.handleLocale}
                  />
                </Segment>
                {searchPlaceId && (
                  <Segment inverted color="olive">
                    <Header as="h5">Current Place</Header>
                    <PlaceView placeId={searchPlaceId} />
                    <Form.Button positive onClick={this.handleAddCommonPlace}>
                      Add Common Place
                    </Form.Button>
                  </Segment>
                )}
              </Segment.Group>
            </Grid.Column>
            <Grid.Column>
              {commonPlace && (
                <Segment inverted color="olive">
                  <Header as="h5">Common Place</Header>
                  <CommonPlaceForm
                    modalStore={modalStore}
                    commonPlace={commonPlace}
                    defaultLocale={defaultLocale}
                    searchPlaceId={searchPlaceId}
                    setCommonPlace={(commonPlace: Partial<ScratchCommonPlace> | null) => {
                      this.setState((state) => {
                        if (commonPlace) {
                          return {
                            commonPlace: {
                              ...(state.commonPlace || emptyCommonPlace),
                              ...commonPlace,
                            },
                          }
                        } else {
                          return {commonPlace: null}
                        }
                      })
                    }}
                  />
                </Segment>
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              {mapPlaceId && <PlaceMap placeId={mapPlaceId} locale={locale} />}
            </Grid.Column>
            <Grid.Column>
              <Table celled selectable compact="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Locale</Table.HeaderCell>
                    <Table.HeaderCell>Label</Table.HeaderCell>
                    <Table.HeaderCell>Category</Table.HeaderCell>
                    <Table.HeaderCell>Suggest?</Table.HeaderCell>
                    <Table.HeaderCell>Delete?</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {commonPlaces.map((cp: CommonPlace, index: number) => (
                    <CommonPlaceRow
                      key={index}
                      commonPlace={cp}
                      index={index}
                      selected={commonPlaceId === cp.id}
                      onSelect={this.handleCommonPlaceRowSelect}
                      onDelete={this.handleCommonPlaceRowDelete}
                    />
                  ))}
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    )
  }

  get modalStore() {
    return this.props.modalStore
  }

  onCommonPlace(commonPlace: CommonPlace) {
    this.setState({commonPlace: toScratchCommonPlace(commonPlace)})
  }

  onAddCommonPlace(localeId: ID, placeId: ID) {
    this.setState({
      commonPlace: {
        ...emptyCommonPlace,
        localeId,
        placeId,
      },
    })
  }

  onDeleteCommonPlace(commonPlace: CommonPlace) {
    const {modalStore} = this
    modalStore.showModal(DeleteCommonPlaceModal, {
      onConfirm: () =>
        modalStore.handlePromiseThrow(
          modalStore.applyModal({
            pending: ["Deleting common place", "Deleting common place..."],
            error: (m) => ["Delete common place failed", m],
            action: () => onDeleteCommonPlaceMutation(commonPlace),
          }),
        ),
      onDismiss: () => modalStore.hideModal(),
    })
  }
}

interface WithCommonPlacesProp {
  commonPlaces: Array<CommonPlace>
}

function WithCommonPlaces(props: DefaultLocaleProp & WithModalStore) {
  return (
    <GraphQL.Query
      query={CommonPlaces}
      variables={{}}
      render={({data}) => {
        if (data) {
          return <CommonPlacesAdmin {...props} commonPlaces={data.commonPlaces} />
        } else {
          return <Loader active />
        }
      }}
    />
  )
}

function refetchQueries(localeId: ID) {
  return [
    // This keeps the common places up to date and *in the right order*.
    makeQueryWithVarsNoT({
      query: COMMON_PLACES,
      variables: {},
    }),
    // This ensures changes to common places are *instantly* visible when the admin user
    // makes a booking and asks for some place suggestions...
    makeQueryWithVarsNoT({
      query: PLACE_SUGGESTIONS,
      variables: {
        localeId,
        text: "",
      },
    }),
  ]
}

export async function onUpsertCommonPlaceMutation(
  variables: UpsertCommonPlaceMutationVariables,
): Promise<OrError<CommonPlaceFragment>> {
  const data = await GraphQL.mutate(UPSERT_COMMON_PLACE, variables, {
    refetchQueries: refetchQueries(variables.commonPlace.localeId),
  })
  return errorUnionToEither(data.upsertCommonPlace)
}

async function onDeleteCommonPlaceMutation(
  commonPlace: CommonPlace,
): Promise<OrError<UUIDObjFragment>> {
  const data = await GraphQL.mutate(
    DELETE_COMMON_PLACE,
    {id: commonPlace.id},
    {refetchQueries: refetchQueries(commonPlace.locale.id)},
  )
  return errorUnionToEither(data.deleteCommonPlace)
}

interface DefaultLocaleProp {
  defaultLocale: Locale
}

function WithDefaultLocale(props: WithModalStore) {
  return (
    <GraphQL.Query
      query={DEFAULT_LOCALE}
      variables={{}}
      render={({data}) => {
        if (data) {
          return <WithCommonPlaces {...props} defaultLocale={data.defaultLocale} />
        } else {
          return <Loader active />
        }
      }}
    />
  )
}

export {WithDefaultLocale as CommonPlacesAdmin}



// WEBPACK FOOTER //
// ./src/components/CommonPlacesAdmin.tsx